import { type CatalogItemDto, type QuoteItem } from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { NextStepFields } from '@/components/leads/NextStepFields';
import { emptyNextStep, toNextStep, type NextStepForm } from '@/components/leads/nextStep';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { fmt } from '@/lib/format';
import { useAnnulQuote, useCatalogs } from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { undoErrorMessage } from './undoForm';

const OTHER = '__otro__';
const isTechnical = (r: CatalogItemDto) => r.metadata.isTechnical === true;

/**
 * Anular una proforma desde la ficha con un diálogo propio (Lote 5 · H4, P26, ux-comercial-28 / ux-ficha-22) en
 * lugar del cuadro nativo del navegador: motivo del catálogo de caídas (o un texto propio), las consecuencias a la
 * vista y el siguiente paso obligatorio (sin él la API respondía `NEXT_STEP_REQUIRED` y no había cómo darlo).
 * `liveDealCount` > 0 quita «Desistir» del siguiente paso (la API lo rechazaría con un proceso vivo).
 */
export function AnnulQuoteDialog({
  companyId,
  quote,
  liveDealCount = 0,
  onOpenChange,
}: {
  companyId: string;
  quote: QuoteItem | null;
  liveDealCount?: number;
  onOpenChange: (o: boolean) => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const tz = useCompanyTimezone(companyId);
  const annul = useAnnulQuote();
  const toast = useToast();
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (quote) {
      setReasonCode('');
      setReasonText('');
      setNext(emptyNextStep(tz));
      setError(null);
    }
  }, [quote, tz]);
  if (!quote) return null;
  const reasons = (catalogs?.dropReasons ?? []).filter((r) => !isTechnical(r));
  const reason =
    reasonCode === OTHER
      ? reasonText.trim()
      : (reasons.find((r) => r.code === reasonCode)?.label ?? '');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (reason.length < 2) {
      setError('Indica el motivo de la anulación');
      return;
    }
    try {
      const lead = await annul.mutateAsync({
        id: quote.id,
        input: { reason, next: toNextStep(next, tz) },
      });
      toast.success(
        lead.hasDesisted
          ? `Proforma ${quote.code} anulada y lead desistido`
          : `Proforma ${quote.code} anulada`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(undoErrorMessage(err, 'No se pudo anular la proforma'));
    }
  };
  return (
    <Dialog open={!!quote} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              Anular la proforma {quote.code}
              {quote.unitCode ? ` · ${quote.unitCode}` : ''}
            </DialogTitle>
            <DialogDescription>
              {fmt.money(quote.price, quote.currency)}
              {quote.expiresAt ? ` · vence ${fmt.date(quote.expiresAt)}` : ''}. La proforma deja de
              contar y el lead vuelve a la etapa anterior; la visita registrada con ella se conserva
              (fue un hecho). Queda anotado en la actividad del lead.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="aq-reason">Motivo</Label>
              <Select
                id="aq-reason"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                required
                data-testid="annul-quote-reason"
              >
                <option value="">Selecciona…</option>
                {reasons.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
                <option value={OTHER}>Otro motivo…</option>
              </Select>
            </div>
            {reasonCode === OTHER && (
              <div className="space-y-1">
                <Label htmlFor="aq-reason-text">¿Cuál?</Label>
                <Input
                  id="aq-reason-text"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  required
                  minLength={2}
                  maxLength={300}
                  placeholder="Ej. precio equivocado"
                />
              </div>
            )}
          </div>
          <NextStepFields
            value={next}
            onChange={setNext}
            desistReasons={catalogs?.desistReasons ?? []}
            idPrefix="aq-next"
            allowDesist={liveDealCount === 0}
          />
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={annul.isPending}
              data-testid="annul-quote-submit"
            >
              {annul.isPending && <Loader2 className="h-4 w-4 animate-spin" />}{' '}
              {next.kind === 'DESIST' && liveDealCount === 0
                ? 'Anular y desistir'
                : 'Anular proforma'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
